import React, { Fragment, useCallback, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { navigate } from '@reach/router';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import * as Yup from 'yup';
import EditorJs from 'react-editor-js';
import axios from 'axios';

import { Button } from '@squirrellearningltd/go-teacher-components';

import {
    EDIT_BOOK_MUTATION,
    GET_BOOK_QUERY,
    GET_S3_UPLOAD_QUERY,
} from '../services/graphql';
import { CheckCircle } from 'react-feather';
import styles from '../components/BookHolder/BookHolder.module.scss';

const BookSettings = ({ backLink, book, onSaveNewBook }) => {
    const [coverUrl, setCoverUrl] = useState(book.coverUrl);
    const [backCoverUrl, setBackCoverUrl] = useState(book.backCoverUrl);
    const [previewUrl, setPreviewUrl] = useState(book.previewUrl);
    const [resourcesUrl, setResourcesUrl] = useState(book.resourcesUrl);

    const [forewordValue, setForewordValue] = useState();

    const client = useApolloClient();

    const [editedForeword] = useState(() => {
        try {
            JSON.parse(book.foreword);
        } catch (e) {
            return {
                blocks: [
                    {
                        type: 'paragraph',
                        data: {
                            text: book.foreword,
                        },
                    },
                ],
            };
        }
        return JSON.parse(book.foreword);
    });
    const editorJsRef = useRef(null);

    const [editBook, { loading: savingBook }] = useMutation(
        EDIT_BOOK_MUTATION,
        {
            update: (_, { data }) => {
                if (
                    data.editBookTemplate.errors &&
                    data.editBookTemplate.errors.length
                ) {
                    alert(data.editBookTemplate.errors[0]);
                }
            },
            refetchQueries: [
                {
                    query: GET_BOOK_QUERY,
                    variables: { bookTemplateId: book.id },
                },
            ],
            awaitRefetchQueries: true,
        }
    );

    const schema = Yup.object().shape({
        name: Yup.string().required('Please enter number a pack name'),
        description: Yup.string().required('Please enter a pack description'),
        coverTemplateName: Yup.string().required(
            'Please enter cover template name'
        ),
        foreword: Yup.string().required('Please enter a default book foreword'),
        videoUrl: Yup.string()
            .nullable()
            .test('is-valid-url', 'Please enter a valid URL', function(value) {
                if (!value || !value.trim()) return true;
                try {
                    new URL(value);
                    return true;
                } catch (_) {
                    return false;
                }
            })
    });

    const { register, handleSubmit, errors, setValue, watch, setError } = useForm({
        validationSchema: schema,
        mode: 'onBlur',
    });

    const onEditorChange = useCallback(async () => {
        const editorData = await editorJsRef.current.save();
        setValue('foreword', JSON.stringify(editorData));
        setForewordValue(JSON.stringify(editorData));
    }, [editorJsRef, setValue]);

    const onSubmit = (values) => {
        const {
            name,
            description,
            coverTemplateName,
            foreword,
            resources,
            videoUrl,
            backgroundColor,
            textColor,
            targetAge,
        } = values;
        if (!book.id) {
            onSaveNewBook({
                ...book,
                name,
                coverTemplateName,
                description,
                foreword,
                resources,
                coverUrl,
                previewUrl,
                resourcesUrl,
                videoUrl,
                backCoverUrl,
                backgroundColor,
                textColor,
                targetAge,
            });
        } else {
            editBook({
                variables: {
                    bookTemplateId: book.id,
                    bookTemplateCategoryId: book.bookTemplateCategoryId,
                    coverTemplateName,
                    foreword,
                    name,
                    description,
                    coverUrl,
                    previewUrl,
                    resourcesUrl,
                    videoUrl,
                    backCoverUrl,
                    backgroundColor,
                    textColor,
                    targetAge,
                },
            });
        }
    };

    const onSelectFile = (files, objectName) => {
        if (files.length) {
            const extension = files[0].name.split('.').pop().toLowerCase();
             const allowedTypes = ['jpg', 'jpeg', 'png'];
            if (!allowedTypes.includes(extension)) {
                setError('iconUrl', 'manual', 'Only .jpg, .jpeg, and .png files are allowed.');
                return;
            }

            setError('iconUrl', 'manual', '');
            const filename = `${
                book.id || ''
            }${objectName}-${new Date().getTime()}.${extension}`;
            client
                .query({
                    query: GET_S3_UPLOAD_QUERY,
                    variables: {
                        name: filename,
                        fileType: extension,
                        object: objectName,
                    },
                })
                .then((r) => {
                    const options = {
                        headers: {
                            'Content-Type': '',
                            'x-amz-acl': 'public-read',
                        },
                    };
                    axios
                        .put(r.data.s3UploadUrlFor.uploadUrl, files[0], options)
                        .then((s3r) => {
                            if (objectName === 'bookTemplateCover') {
                                setCoverUrl(r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                                if (book.id) {
                                    editBook({
                                        variables: {
                                            ...book,
                                            bookTemplateId: book.id,
                                            coverUrl:
                                                r.data.s3UploadUrlFor.name,
                                        },
                                    });
                                }
                            }
                            if (objectName === 'bookTemplateBackCover') {
                                setBackCoverUrl(r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                                if (book.id) {
                                    editBook({
                                        variables: {
                                            ...book,
                                            bookTemplateId: book.id,
                                            backCoverUrl:
                                                r.data.s3UploadUrlFor.name,
                                        },
                                    });
                                }
                            }
                            if (objectName === 'bookTemplatePreview') {
                                setPreviewUrl(r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                                if (book.id) {
                                    editBook({
                                        variables: {
                                            ...book,
                                            bookTemplateId: book.id,
                                            previewUrl:
                                                r.data.s3UploadUrlFor.name,
                                        },
                                    });
                                }
                            }
                            if (objectName === 'bookTemplateResources') {
                                // Check if r.data.s3UploadUrlFor.uploadUrl.split('?')[0] is required.
                                setResourcesUrl(r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                                if (book.id) {
                                    editBook({
                                        variables: {
                                            ...book,
                                            bookTemplateId: book.id,
                                            resourcesUrl:
                                                r.data.s3UploadUrlFor.name,
                                        },
                                    });
                                }
                            }
                        });
                });
        }
    };
    return (
        <Fragment>
            <div className={styles.BookSettingsContainer}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='basic-form__group basic-form__group--no-bm'>
                        <input
                            name='name'
                            placeholder='Book Name'
                            className='basic-form__text-box'
                            defaultValue={book.name}
                            ref={register}
                            type='text'
                        />
                    </div>
                    {errors.name && (
                        <p className='basic-form__hint'>
                            {errors.name.message}
                        </p>
                    )}
                    <div className='basic-form__group'>
                        <input
                            placeholder='Video url (optional)'
                            className='basic-form__text-box'
                            defaultValue={book.videoUrl}
                            ref={register}
                            name='videoUrl'
                        />
                    </div>
                    {errors.videoUrl && (
                        <p className='basic-form__hint'>
                            {errors.videoUrl.message}
                        </p>
                    )}
                    <div className='basic-form__group'>
                        <textarea
                            name='description'
                            placeholder='Book Description'
                            className='basic-form__text-box'
                            defaultValue={book.description}
                            ref={register}
                        />
                    </div>
                    {errors.description && (
                        <p className='basic-form__hint'>
                            {errors.description.message}
                        </p>
                    )}
                    <div className='basic-form__group'>
                        <input
                            name='coverTemplateName'
                            placeholder='Cover Template Name'
                            className='basic-form__text-box'
                            defaultValue={book.coverTemplateName}
                            ref={register}
                            type='text'
                        />
                    </div>
                    {errors.coverTemplateName && (
                        <p className='basic-form__hint'>
                            {errors.coverTemplateName.message}
                        </p>
                    )}
                    <div className='basic-form__group'>
                        <div className='basic-form__editor-holder'>
                            <EditorJs
                                data={editedForeword}
                                placeholder='Default foreword (required)'
                                instanceRef={(instance) =>
                                    (editorJsRef.current = instance)
                                }
                                enableReInitialize={false}
                                minHeight='0'
                                onChange={onEditorChange}
                                logLevel='WARN'
                            />
                        </div>
                        <input
                            type='hidden'
                            name='foreword'
                            ref={register}
                            defaultValue={book.foreword || forewordValue}
                        />
                    </div>
                    {errors.foreword && (
                        <p className='basic-form__hint'>
                            {errors.foreword.message}
                        </p>
                    )}
                    <div className='basic-form__group'>
                        <input
                            name='backgroundColor'
                            placeholder='Background'
                            className='basic-form__text-box'
                            defaultValue={book.backgroundColor}
                            ref={register}
                            type='text'
                        />
                    </div>
                    <div className='basic-form__group'>
                        <input
                            name='textColor'
                            placeholder='Text'
                            className='basic-form__text-box'
                            defaultValue={book.textColor}
                            ref={register}
                            type='text'
                        />
                    </div>
                   <div className='basic-form__group'>
                        <select
                            name='targetAge'
                            className='basic-form__text-box'
                            defaultValue={book.targetAge || ""}
                            ref={register}
                        >
                            <option value="" disabled selected>Select Target Age</option>
                            <option value="Ages 5 - 7">Ages 5 - 7</option>
                            <option value="Ages 7 - 9">Ages 7 - 9</option>
                            <option value="Ages 9 - 11">Ages 9 - 11</option>
                        </select>
                    </div>
                    {coverUrl ? (
                        <div className='u-m-base-3 u-display-flex u-align-center'>
                            <p className='u-m-right-3 u-display-flex u-align-center'>
                                <CheckCircle
                                    size='18'
                                    className='u-m-right-1'
                                />{' '}
                                Cover uploaded
                            </p>
                            <Button
                                type='button'
                                onClick={() => setCoverUrl(null)}
                            >
                                Change
                            </Button>
                        </div>
                    ) : (
                        <div className='basic-form__group basic-form__group--check'>
                            <label
                                className='basic-form__label'
                                htmlFor='resources'
                            >
                                Cover Image
                            </label>
                            <input
                                name='resources'
                                className='basic-form__text-box'
                                placeholder='Cover Image'
                                onChange={(e) =>
                                    onSelectFile(
                                        e.target.files,
                                        'bookTemplateCover'
                                    )
                                }
                                type='file'
                                accept='.jpg,.jpeg,.png'
                            />
                        </div>
                    )}
                    {errors.bookTemplateCover && <p className='basic-form__hint'>{errors.bookTemplateCover.message}</p>}
                    {backCoverUrl ? (
                        <div className='u-m-base-3 u-display-flex u-align-center'>
                            <p className='u-m-right-3 u-display-flex u-align-center'>
                                <CheckCircle
                                    size='18'
                                    className='u-m-right-1'
                                />
                                Back Cover uploaded
                            </p>
                            <Button
                                type='button'
                                onClick={() => setBackCoverUrl(null)}
                            >
                                Change
                            </Button>
                        </div>
                    ) : (
                        <div className='basic-form__group basic-form__group--check'>
                            <label
                                className='basic-form__label'
                                htmlFor='resources'
                            >
                                Back Cover Image
                            </label>
                            <input
                                name='resources'
                                className='basic-form__text-box'
                                placeholder='Back Cover Image'
                                onChange={(e) =>
                                    onSelectFile(
                                        e.target.files,
                                        'bookTemplateBackCover'
                                    )
                                }
                                type='file'
                                accept='.jpg,.jpeg,.png'
                            />
                        </div>
                    )}
                    {errors.bookTemplateBackCover && <p className='basic-form__hint'>{errors.bookTemplateBackCover.message}</p>}
                    {previewUrl ? (
                        <div className='u-m-base-3 u-display-flex u-align-center'>
                            <p className='u-m-right-3 u-display-flex u-align-center'>
                                <CheckCircle
                                    size='18'
                                    className='u-m-right-1'
                                />{' '}
                                Preview uploaded
                            </p>
                            <Button
                                type='button'
                                onClick={() => setPreviewUrl(null)}
                            >
                                Change
                            </Button>
                        </div>
                    ) : (
                        <div className='basic-form__group basic-form__group--check'>
                            <label
                                className='basic-form__label'
                                htmlFor='resources'
                            >
                                Book Preview
                            </label>
                            <input
                                name='resources'
                                className='basic-form__text-box'
                                placeholder='Book Preview'
                                onChange={(e) =>
                                    onSelectFile(
                                        e.target.files,
                                        'bookTemplatePreview'
                                    )
                                }
                                type='file'
                                accept='.jpg,.jpeg,.png'
                            />
                        </div>
                    )}
                    {errors.bookTemplatePreview && <p className='basic-form__hint'>{errors.bookTemplatePreview.message}</p>}
                    {resourcesUrl ? (
                        <div className='u-m-base-3 u-display-flex u-align-center'>
                            <p className='u-m-right-3 u-display-flex u-align-center'>
                                <CheckCircle
                                    size='18'
                                    className='u-m-right-1'
                                />{' '}
                                Resources uploaded
                            </p>
                            <Button
                                type='button'
                                onClick={() => setResourcesUrl(null)}
                            >
                                Change
                            </Button>
                        </div>
                    ) : (
                        <div className='basic-form__group basic-form__group--check'>
                            <label
                                className='basic-form__label'
                                htmlFor='resources'
                            >
                                Teacher Resources
                            </label>
                            <input
                                name='resources'
                                className='basic-form__text-box'
                                placeholder='Teacher resources'
                                onChange={(e) =>
                                    onSelectFile(
                                        e.target.files,
                                        'bookTemplateResources'
                                    )
                                }
                                type='file'
                                accept='.jpg,.jpeg,.png'
                            />
                        </div>
                    )}
                    {errors.bookTemplateResources && <p className='basic-form__hint'>{errors.bookTemplateResources.message}</p>}
                    <div className='u-display-flex u-align-center'>
                        <Button
                            disabled={!coverUrl}
                            type='submit'
                            className='u-m-right-1'
                        >
                            {savingBook ? 'Saving...' : 'Save Book'}
                        </Button>
                        <Button
                            type='button'
                            outline
                            onClick={() => navigate('/packs')}
                        >
                            Cancel
                        </Button>
                    </div>
                </form>

                <div>
                    <h3>Preview</h3>

                    <div className={styles.coverWrapper}>
                        <h4>Front Cover</h4>
                        <div className={styles.coverWrapperPreview}>
                            {coverUrl && (
                                <img src={coverUrl} alt='front-cover' />
                            )}
                        </div>
                    </div>

                    <div className={styles.coverWrapper}>
                        <h4>Back Cover</h4>
                        <div className={styles.coverWrapperPreview}>
                            {backCoverUrl && (
                                <img src={backCoverUrl} alt='back-cover' />
                            )}
                        </div>
                    </div>

                    <div className={styles.coverWrapper}>
                        <h4>Book Selection</h4>
                        <div
                            style={{
                                backgroundColor: watch('backgroundColor') || book.backgroundColor,
                                color: watch('textColor') || book.textColor,
                            }}
                            className={styles.bookName}
                        >
                            {watch('name') || book.name}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default BookSettings;
